import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link, withRouter } from 'react-router-dom';
import {
  Form, Input, Button, Checkbox, Tooltip, Row, Col,
} from 'antd';

import { compose } from 'ramda';
import {
  login, getUser, getBalance,
} from '../../actions/user';
import { ACCOUNT_DISABLED, INVALID_RECAPTCHA_RESPONSE } from '../../constants';
import message from '../../actions/message';

import c from './LoginForm.less';

const FormItem = Form.Item;

class LoginFormContainer extends React.Component {
  recaptchaRef = null;

  static propTypes = {
    form: PropTypes.object.isRequired,
    layout: PropTypes.string,
    onLinkClick: PropTypes.func,
    onLogin: PropTypes.func,
    getUser: PropTypes.func.isRequired,
    getBalance: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired,
    messageError: PropTypes.func.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    auth: PropTypes.object.isRequired,
    userFetching: PropTypes.object.isRequired,
  };

  static defaultProps = {
    layout: 'full',
    onLinkClick: () => null,
    onLogin: () => null,
  };

  state = {
    loginAttempts: 0,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.loggedIn && this.props.loggedIn !== nextProps.loggedIn) {
      this.props.form.resetFields();
      this.props.getUser();
      this.props.getBalance();
      this.props.onLogin();
    }

    if (nextProps.auth.error && this.props.auth.error !== nextProps.auth.error) {
      this.props.messageError({ content: nextProps.auth.error.message, key: nextProps.auth.error.errorCode });

      if (nextProps.auth.error.errorCode === INVALID_RECAPTCHA_RESPONSE) {
        this.setState({
          loginAttempts: Number.MAX_SAFE_INTEGER,
        });
        if (this.recaptchaRef) {
          this.recaptchaRef.reset();
        }
      } else {
        this.setState((state) => ({
          loginAttempts: state.loginAttempts + 1,
        }));
      }
    }

    if (nextProps.userFetching.error && this.props.userFetching.errorBody !== nextProps.userFetching.errorBody) {
      if (nextProps.userFetching.errorBody === ACCOUNT_DISABLED) {
        this.props.messageError({ content: 'Account is disabled' });
      }
    }
  }

  get layoutSpans() {
    const { layout } = this.props;

    if (layout === 'full') {
      return {
        left: {
          xs: 24,
          md: 7,
        },
        right: {
          xs: 24,
          md: 17,
        },
        offset: {
          xs: {
            offset: 0,
          },
          md: {
            offset: 7,
          },
        },
      };
    }

    return {
      left: 24,
      right: 24,
      offset: 0,
    };
  }

  handleReCaptcha = (response) => {
    this.props.form.setFieldsValue({
      validation: response,
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    this.props.form.validateFields((err, values) => {
      if (!err) {
        this.props.login(values);
      }
    });
  };

  render() {
    const {
      form: { getFieldDecorator }, layout, onLinkClick, auth,
    } = this.props;
    const { loginAttempts } = this.state;

    return (
      <Form onSubmit={this.handleSubmit} className={c[`form-${layout}`]}>
        <Row>
          <Col {...this.layoutSpans.left} className={c.label}>Username </Col>
          <Col {...this.layoutSpans.right}>
            <FormItem
              hasFeedback
              className={c.input}
            >
              {getFieldDecorator('username', {
                rules: [{ required: true, message: 'Please enter your username or email address' }],
              })(
                <Input placeholder="Username or Email Address" />,
              )}
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col {...this.layoutSpans.left} className={c.label}>Password </Col>
          <Col {...this.layoutSpans.right}>
            <FormItem
              hasFeedback
              className={c.input}
            >
              {getFieldDecorator('password', {
                rules: [{ required: true, message: 'Please enter your password' }],
              })(
                <Input type="password" placeholder="Password" />,
              )}
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col {...this.layoutSpans.offset} className={c.rememberMeForgotPassword}>
            <FormItem className={c.rememberMe}>
              {getFieldDecorator('remember', {
                valuePropName: 'checked',
                initialValue: false,
              })(
                <Checkbox>
                  <Tooltip
                    title="Select this option if you're the only person who uses this device. Server will allow a longer period of inactivity before signing you out."
                  >
                    Keep me signed in
                  </Tooltip>
                </Checkbox>,
              )}
            </FormItem>

            <Link className={c.forgotPassLink} to="/shop/reset-password" onClick={onLinkClick}>Forgot password</Link>
          </Col>
        </Row>
        {
          loginAttempts >= 6 && (
            <Row>
              <Col {...this.layoutSpans.offset}>
                <FormItem
                  className={c.input}
                >
                  {getFieldDecorator('validation', {
                    validateFirst: true,
                    rules: [{
                      required: true,
                      min: 1,
                      message: 'Please confirm that you are human',
                    }, {
                      validator: this.beValidation,
                    }],
                  })(
                    <ReCAPTCHA
                      sitekey={process.env.RECAPTCHA_CLIENT}
                      onChange={this.handleReCaptcha}
                      size="normal"
                      ref={(el) => {
                        this.recaptchaRef = el;
                      }}
                    />,
                  )}
                </FormItem>
              </Col>
            </Row>
          )
        }
        <Row>
          <Col {...this.layoutSpans.offset} className={c.submitRegister}>
            <Button loading={auth.loading} type="primary" htmlType="submit" className={c.submitBtn}>
              Log in
            </Button>
            <Link className={c.registerLink} to="/shop/signup" onClick={onLinkClick}>Or register now!</Link>
          </Col>
        </Row>
      </Form>
    );
  }
}

const mapStateToProps = (store) => ({
  auth: store.auth,
  loggedIn: store.user.loggedIn,
  userData: store.user.data,
  userFetching: store.user.fetching,
});

const mapDispatchToProps = {
  login,
  getUser,
  getBalance,
  messageError: message.error,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  Form.create(),
)(LoginFormContainer);
